import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import CallResponseDialog from './CallResponseDialog';
import WorkflowPanel from './WorkflowPanel';
import { BackToTaskButton } from './student-profile/BackToTaskButton';
import RegistrationAdmissionCard from './RegistrationAdmissionCard';
import GstBilling from './GstBilling';
import { ProfileSummary } from './student-profile/ProfileSummary';
import { ProfileHeader } from './student-profile/ProfileHeader';
import { ProfileManagement } from './student-profile/ProfileManagement';
import { ProfileTabs } from './student-profile/ProfileTabs';
import { ProfileDetails } from './student-profile/ProfileDetails';
import { ProfileSectionEditor } from './student-profile/ProfileSectionEditor';
import { ProfileCommunication } from './student-profile/ProfileCommunication';
import { ProfileDocuments } from './student-profile/ProfileDocuments';
import { ProfileSource } from './student-profile/ProfileSource';
import { humanize } from './student-profile/profileFields';

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTaskId = searchParams.get('task');
  const requestedTab = searchParams.get('tab');
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('activity');
  const [editing, setEditing] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [callTaskId, setCallTaskId] = useState(null);
  const [revision, setRevision] = useState(0);
  const fetchStudent = useCallback(async () => {
    const r = await apiClient.get(`/students/${id}`);
    setStudent(r.data); return r.data;
  }, [id]);
  useEffect(() => {
    let active = true;
    setLoading(true); setStudent(null); setError(''); setEditing(null); setCallOpen(false);
    apiClient.get(`/students/${id}`).then(r => {
      if (!active) return;
      setStudent(r.data);
      setActiveTab(requestedTab === 'tasks' || requestedTab === 'communication' || requestedTaskId || ['admin', 'operating_head', 'team_leader', 'telecaller', 'counselor'].includes(user?.role) ? 'activity' : 'details');
    }).catch(e => { if (active) setError(e.response?.status === 403 ? 'You do not have access to this student profile.' : e.response?.status === 404 ? 'Student not found' : 'Could not load student profile'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, requestedTab, requestedTaskId, user?.role]);
  const changed = async () => {
    try { await fetchStudent(); setRevision(v => v + 1); }
    catch { toast.error('Could not refresh the profile'); }
  };
  const toggleProspective = async () => {
    try { await apiClient.put(`/students/${id}`, { is_prospective: !student.is_prospective }); await changed(); }
    catch { toast.error('Could not update prospective status'); }
  };
  if (loading) return <div data-testid="student-profile-loading" className="py-12 text-sm text-gray-500">Loading student profile…</div>;
  if (error || !student) return <div role="alert" data-testid="student-profile-error" className="space-y-3"><p>{error || 'Student not found'}</p><Button onClick={() => navigate(user?.role === 'telecaller' ? '/tasks' : '/leads')} data-testid="profile-error-back">{user?.role === 'telecaller' ? 'Back to Tasks' : 'Back to Leads'}</Button></div>;
  return <div className="simple-student-profile w-full max-w-5xl mx-auto min-w-0 pb-4" data-testid="student-profile">
    <ProfileHeader student={student} onBack={() => window.history.length > 1 ? navigate(-1) : navigate('/tasks')} onEdit={() => { setActiveTab('details'); setEditing('choose'); }} onProspective={toggleProspective} />
    <Tabs value={activeTab} onValueChange={setActiveTab} className="profile-content-tabs">
      <ProfileTabs />
      <TabsContent value="activity" className="min-w-0 space-y-6">
        <span id="activity-top" aria-hidden="true" />
        <WorkflowPanel key={`${id}-${revision}`} leadId={id} student={student} requestedTaskId={requestedTaskId} onChanged={fetchStudent} onCallOutcome={taskId => { setCallTaskId(taskId || null); setCallOpen(true); }} onCommunication={() => document.getElementById('activity-timeline')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <div id="activity-timeline" className="border-t pt-5 scroll-mt-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3" data-testid="activity-timeline-heading">Activity &amp; communication</h2>
          <ProfileCommunication key={id} studentId={id} onChanged={fetchStudent} active={activeTab === 'activity'} showComposer={false} />
        </div>
        {activeTab === 'activity' && <BackToTaskButton targetId="activity-top" />}
      </TabsContent>
      <TabsContent value="details" className="space-y-5">
        <ProfileSummary student={student} />
        <ProfileDetails student={student} onEdit={setEditing} />
        <ProfileSource student={student} user={user} onChanged={changed} />
        <ProfileManagement student={student} user={user} onChanged={changed} onDeleted={() => navigate('/leads')} />
      </TabsContent>
      <TabsContent value="documents"><ProfileDocuments student={student} onChanged={changed} /></TabsContent>
      <TabsContent value="registration">
        {!student.is_converted && !['telecaller', 'documentation_team', 'accountant'].includes(user?.role) ? <RegistrationAdmissionCard studentId={id} user={user} onConverted={changed} /> : student.is_converted ? <section className="space-y-4" data-testid="profile-admission-summary"><h2 className="text-base font-semibold">Admission</h2><p className="text-sm" data-testid="profile-admission-stage">{humanize(student.admission_stage)}</p><p className="text-sm text-emerald-700" data-testid="profile-converted-status">Converted to admission</p>{!['telecaller', 'documentation_team'].includes(user?.role) && <GstBilling studentId={id} />}</section> : <p className="text-sm text-gray-500" data-testid="registration-role-message">Registration is managed by the counselling and finance teams.</p>}
      </TabsContent>
    </Tabs>
    {editing && <ProfileSectionEditor key={editing} sectionId={editing} student={student} user={user} onSection={setEditing} onClose={() => setEditing(null)} onSaved={changed} />}
    <CallResponseDialog open={callOpen} onOpenChange={setCallOpen} sourceTaskId={callTaskId} lead={{ id, full_name: student.full_name, student_id: student.student_id, rnr_consecutive: student.rnr_consecutive }} onSaved={changed} />
  </div>;
}